import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import UserReducer from "./slices/user";
import templateReducer from "./slices/templatesSlice";
import locationReducer from "./slices/location";

import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage/session";
import { authApi } from "./Reducer/auth";
import { hijriCalendar } from "./Reducer/hijri-calendar";
import { userSetting } from "./Reducer/user-setting";
import { news } from "./Reducer/news";
import { events } from "./Reducer/events";
import { file } from "./Reducer/file";
import { announcement } from "./Reducer/annocements";
import { prayerTimes } from "./Reducer/prayertimes";
import { teams } from "./Reducer/teams";
import { userProfileApi } from "./Reducer/user-profile";
import { resources } from "./Reducer/resources";
import { templateApis } from "./Reducer/templateApis";
import { display } from "./Reducer/display";
import { Playlist } from "./Reducer/playlist";
import { subscription } from "./Reducer/subscription";
import { aiApi } from "./Reducer/aiApi";
import { adminApi } from "./Reducer/adminApi";
import { tickets } from "./Reducer/tickets";
import { messages } from "./Reducer/messages";

// Each persisted slice needs its OWN storage key. They used to share "root",
// so the three slices overwrote each other's saved state in sessionStorage.
const persistConfig = (key, blacklist) => ({ key, storage, blacklist });

// Create persisted reducers for the user and template slices
// Undo history is up to 60 full copies of the site: too big for sessionStorage.
const persistedTemplateReducer = persistReducer(
  persistConfig("template", ["past", "future", "sectionVersions", "lastEditKey"]),
  templateReducer
);
const persistedAuthReducer = persistReducer(persistConfig("auth"), UserReducer);
const persistedLocationReducer = persistReducer(persistConfig("location"), locationReducer);

// Create the Redux store with the persisted reducers
export const store = configureStore({
  reducer: {
    [authApi.reducerPath]: authApi.reducer,
    [hijriCalendar.reducerPath]: hijriCalendar.reducer,
    [userSetting.reducerPath]: userSetting.reducer,
    [news.reducerPath]: news.reducer,
    [events.reducerPath]: events.reducer,
    [file.reducerPath]: file.reducer,
    [announcement.reducerPath]: announcement.reducer,
    [prayerTimes.reducerPath]: prayerTimes.reducer,
    [teams.reducerPath]: teams.reducer,
    [userProfileApi.reducerPath]: userProfileApi.reducer,
    [resources.reducerPath]: resources.reducer,
    [templateApis.reducerPath]: templateApis.reducer,
    [display.reducerPath]: display.reducer,
    [Playlist.reducerPath]: Playlist.reducer,
    [subscription.reducerPath]: subscription.reducer,
    [aiApi.reducerPath]: aiApi.reducer,
    [adminApi.reducerPath]: adminApi.reducer,
    [tickets.reducerPath]: tickets.reducer,
    [messages.reducerPath]: messages.reducer,

    template: persistedTemplateReducer,
    Auth: persistedAuthReducer,
    Location: persistedLocationReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    }).concat(
      authApi.middleware,
      hijriCalendar.middleware,
      userSetting.middleware,
      news.middleware,
      events.middleware,
      file.middleware,
      announcement.middleware,
      prayerTimes.middleware,
      teams.middleware,
      userProfileApi.middleware,
      resources.middleware,
      templateApis.middleware,
      display.middleware,
      Playlist.middleware,
      subscription.middleware,
      aiApi.middleware,
      adminApi.middleware,
      tickets.middleware,
      messages.middleware
    ),
});

// Initialize Redux Persist
export const persistor = persistStore(store);

// Setup listeners
setupListeners(store.dispatch);
